// Source of truth for the QA test plan. Adding a case here makes it
// appear in /qa/[sessionId] for the next tester to run; removing one
// hides it from new sessions but keeps historical results readable
// (results store a snapshot of the title at submit time).
//
// Conventions:
//  - `id` is a stable kebab-case slug. Never reuse an id for a different
//    test — that would silently merge results across distinct cases.
//  - `expected` is the clear pass criterion. If you can't write it in one
//    sentence, the test is too broad.
//  - `steps` (optional) gives the tester a recipe. Skip when "do the
//    obvious thing" is enough.

export const QA_CATEGORIES = [
  "Pre-launch",
  "Auth",
  "Wizard",
  "Checkout",
  "Build Room",
  "Customer Actions",
  "Admin",
  "Edges",
] as const;
export type QaCategory = (typeof QA_CATEGORIES)[number];

export type QaTestCase = {
  id: string;
  category: QaCategory;
  title: string;
  description: string;
  expected: string;
  steps?: string[];
};

export const QA_TEST_CASES: QaTestCase[] = [
  /* ============================== Pre-launch ============================== */
  {
    id: "qa-pre-01",
    category: "Pre-launch",
    title: "Marketing landing page renders",
    description:
      "Visit the root URL as an unauthenticated visitor.",
    expected:
      "The hero, headline, and CTAs render. Header shows Sign in / Get started. Footer shows Terms / Privacy / Refunds + support email. No console errors.",
    steps: ["Open / in a private window."],
  },
  {
    id: "qa-pre-02",
    category: "Pre-launch",
    title: "Legal pages render",
    description: "Visit /terms, /privacy, and /refunds.",
    expected:
      "Each page loads with title, last-updated date, and full markdown body. Links inside legal copy work.",
  },
  {
    id: "qa-pre-03",
    category: "Pre-launch",
    title: "Sitemap and robots are exposed",
    description:
      "Open /sitemap.xml and /robots.txt directly in the browser.",
    expected:
      "sitemap.xml lists /, /signin, /terms, /privacy, /refunds. robots.txt allows the public surface and disallows /dashboard, /admin, /orders, /brief, /api.",
  },

  /* ================================ Auth =================================== */
  {
    id: "qa-auth-01",
    category: "Auth",
    title: "Magic-link sign-in delivers and works",
    description:
      "From /signin, request a magic link with a real (deliverable) email.",
    expected:
      "You land on /signin/check-email. The email arrives within ~30s. Clicking the link signs you in and lands on /dashboard.",
  },
  {
    id: "qa-auth-02",
    category: "Auth",
    title: "Expired magic link → friendly error page",
    description:
      "Wait >1 hour after requesting a magic link, then click it.",
    expected:
      "/auth/error renders with copy explaining the link expired and a CTA back to /signin.",
  },
  {
    id: "qa-auth-03",
    category: "Auth",
    title: "404 not-found page is branded",
    description: "Visit /asdf-not-a-real-page.",
    expected:
      "Branded 404 with the Swift MVP logo. CTA reads 'Back to home' if signed out, 'Back to dashboard' if signed in.",
  },
  {
    id: "qa-auth-04",
    category: "Auth",
    title: "Protected route redirects with callbackUrl",
    description:
      "Sign out. Visit /dashboard directly.",
    expected:
      "Redirects to /signin?callbackUrl=%2Fdashboard. After magic-link sign-in, you land on /dashboard (not the default).",
  },
  {
    id: "qa-auth-05",
    category: "Auth",
    title: "Sign-out works from dashboard",
    description: "Click the sign-out button in the dashboard header.",
    expected:
      "You're signed out and redirected. Visiting /dashboard again redirects to /signin.",
  },

  /* =============================== Wizard =================================== */
  {
    id: "qa-wiz-01",
    category: "Wizard",
    title: "Start a brief from the dashboard",
    description:
      "From /dashboard, click 'New brief' (or visit /brief/new).",
    expected:
      "You land on the package picker. Picking a package advances to Step 1 of the wizard with the package preselected.",
  },
  {
    id: "qa-wiz-02",
    category: "Wizard",
    title: "Wizard validation blocks empty required fields",
    description:
      "Try to advance from Step 2 without filling Business Name.",
    expected:
      "The Next button stays disabled OR shows an inline error pointing at the empty field. No silent advance.",
  },
  {
    id: "qa-wiz-03",
    category: "Wizard",
    title: "Logo upload via drag-and-drop works",
    description:
      "On Step 4, drag a PNG/JPG (<5 MB) into the logo field.",
    expected:
      "Progress bar appears, completes, the file shows as uploaded with a thumbnail. Saved URL persists across reload.",
  },
  {
    id: "qa-wiz-04",
    category: "Wizard",
    title: "Auto-save survives reload mid-wizard",
    description:
      "Fill out Step 3 partially, wait ~2s, refresh the page.",
    expected:
      "After reload you land back on Step 3 with all your prior answers intact. SaveStatus shows 'Saved'.",
  },
  {
    id: "qa-wiz-05",
    category: "Wizard",
    title: "Brief review (Step 6) shows everything you entered",
    description:
      "Reach Step 6. Verify each section reflects what you typed in earlier steps.",
    expected:
      "All fields render with correct values. Per-section Edit jumps you back to that step. The 'I understand…' checkbox + Terms/Refund microtext are visible.",
  },
  {
    id: "qa-wiz-06",
    category: "Wizard",
    title: "Reference URL with javascript: scheme is rejected",
    description:
      "On Step 3, try to add a reference URL of `javascript:alert(1)`.",
    expected:
      "Validation rejects it with a 'Must be a valid http(s) URL' message. The brief view never renders an unsafe href.",
  },

  /* ============================== Checkout ================================== */
  {
    id: "qa-co-01",
    category: "Checkout",
    title: "Checkout opens Stripe with correct amount",
    description:
      "On Step 6, check the box and click 'Proceed to Payment'.",
    expected:
      "Stripe Checkout opens (test mode) with the package name and the price matching what was shown on the wizard.",
  },
  {
    id: "qa-co-02",
    category: "Checkout",
    title: "Cancelled checkout returns to review with banner",
    description:
      "From Stripe, click the back arrow / abandon the session.",
    expected:
      "You land back on the review step with a dismissible banner ('Payment cancelled — you can try again'). The brief is unchanged. Status pill says 'Awaiting payment'.",
  },
  {
    id: "qa-co-03",
    category: "Checkout",
    title: "Successful test payment lands on Build Room",
    description:
      "Complete checkout with card 4242 4242 4242 4242, any future expiry / CVC.",
    expected:
      "Redirects to /orders/[id]. Payment is marked paid only when Stripe delivers checkout.session.completed to /api/webhooks/stripe (local dev: run `stripe listen --forward-to …` for that). While waiting, the 'Finalizing…' spinner polls until paidAt is set, then the pending Build Room appears.",
  },
  {
    id: "qa-co-04",
    category: "Checkout",
    title: "Double-click on Proceed to Payment doesn't duplicate orders",
    description:
      "On Step 6, click Proceed to Payment twice rapidly.",
    expected:
      "Only one Stripe session is created. /admin shows exactly one PENDING order for this draft. No duplicate order numbers.",
  },

  /* ============================= Build Room ================================= */
  {
    id: "qa-br-01",
    category: "Build Room",
    title: "Pending state shows countdown and reassurance copy",
    description:
      "Open the Build Room of a paid order that hasn't been started yet.",
    expected:
      "Status pill 'Received'. Countdown timer renders (formatted as e.g. '23h 14m' or 'Less than an hour'). Helper copy says 'We typically start within 2 hours of payment'. Brief is collapsed below.",
  },
  {
    id: "qa-br-02",
    category: "Build Room",
    title: "In-progress state surfaces customer-visible updates",
    description:
      "Have an admin post a customer-visible update on the order. Wait up to 30s.",
    expected:
      "The polling loop detects the new update and refreshes the page automatically. The update text appears in the Updates feed. Customer also receives an email.",
  },
  {
    id: "qa-br-03",
    category: "Build Room",
    title: "Internal admin notes are not visible to the customer",
    description:
      "Have an admin post an internal note (visibleToCustomer=false).",
    expected:
      "The customer's Build Room does NOT show the note. No email is sent. The admin sees an amber-bordered 'Internal' badge on their side.",
  },
  {
    id: "qa-br-04",
    category: "Build Room",
    title: "Final QA (in-review) state has violet accent",
    description:
      "Admin moves the order to In Review.",
    expected:
      "Customer Build Room status pill flips to 'Final QA' in violet. Headline reads 'Almost ready'. Subhead says 'You'll get an email when it's delivered'.",
  },
  {
    id: "qa-br-05",
    category: "Build Room",
    title: "Delivered hero with 'Open your site' CTA",
    description:
      "Admin marks the order complete (after saving delivery + URL).",
    expected:
      "Customer sees the delivered hero (emerald gradient), 'Your <Package> is ready' headline, large 'Open your site →' CTA, and the live URL with a copy button. Clicking the CTA opens the URL in a new tab.",
  },
  {
    id: "qa-br-06",
    category: "Build Room",
    title: "Source code download produces a real file",
    description:
      "On the delivered Build Room, click the 'Download zip' button on the Source code section.",
    expected:
      "Browser kicks off a download (not a navigation). The file lands in your Downloads folder with the correct extension and a friendly name.",
  },
  {
    id: "qa-br-07",
    category: "Build Room",
    title: "Credentials reveal toggle + copy works",
    description:
      "On the Logins & credentials section, click the eye icon next to a sensitive credential, then click the copy icon.",
    expected:
      "Reveal flips between masked dots and the real value. Copy icon flashes a checkmark. Pasting elsewhere shows the credential.",
  },
  {
    id: "qa-br-08",
    category: "Build Room",
    title: "Markdown instructions render with formatting",
    description:
      "Verify the 'How to use this' section renders heading, list, code block, and link from the admin's markdown.",
    expected:
      "All elements render with proper styling. Long URLs in code blocks scroll horizontally without breaking the page. Links open in a new tab with rel=noopener.",
  },
  {
    id: "qa-br-09",
    category: "Build Room",
    title: "Loom walkthrough card opens in a new tab",
    description:
      "If a Loom URL is attached to the delivery, click the walkthrough card.",
    expected:
      "Card-link opens the Loom video in a new tab. No iframe embed (so corporate networks that block Loom iframes don't break the page).",
  },

  /* ========================== Customer Actions ============================== */
  {
    id: "qa-cust-01",
    category: "Customer Actions",
    title: "Request changes modal submits a revision request",
    description:
      "On the delivered Build Room, click 'Request changes', type a real message (≥10 chars), submit.",
    expected:
      "Modal closes with success toast 'We'll get back to you within 24 hours'. Admin receives an email and sees the request open in their order detail. Audit log shows 'Customer requested revisions'.",
  },
  {
    id: "qa-cust-02",
    category: "Customer Actions",
    title: "Mark order complete unlocks feedback form",
    description:
      "On the delivered Build Room, click 'Mark order complete' and confirm.",
    expected:
      "Buttons swap for a 'Thanks — your delivery is confirmed' card. The Request changes button stays available below. The 'Tell us how we did' feedback form appears below the next-actions card.",
  },
  {
    id: "qa-cust-03",
    category: "Customer Actions",
    title: "Feedback submission stores rating and comment",
    description:
      "Pick 5 stars, type a comment, submit.",
    expected:
      "Form swaps to a 'Thanks for the feedback' card with your rating echoed back. Admin's order sidebar shows the rating + comment. Admin email arrives.",
  },

  /* ================================ Admin =================================== */
  {
    id: "qa-admin-01",
    category: "Admin",
    title: "Orders list filters by status pills",
    description:
      "On /admin, click the 'Pending' pill, then add 'In Progress'.",
    expected:
      "URL gains ?status=PENDING&status=IN_PROGRESS. Table re-renders to show only matching orders. 'Clear' resets.",
  },
  {
    id: "qa-admin-02",
    category: "Admin",
    title: "Search finds an order by orderNumber",
    description:
      "On /admin, type the SWIFT-### number of a known order in the search box and press Enter.",
    expected:
      "URL gains ?q=SWIFT-XXXX. Table shows only matching orders (also tries businessName + contactEmail).",
  },
  {
    id: "qa-admin-03",
    category: "Admin",
    title: "Start build button stamps startedAt and flips status",
    description:
      "Open a paid PENDING order in admin. Click 'Start build'.",
    expected:
      "Status pill flips to 'In progress'. Sidebar shows 'Started' timestamp populated. Audit log shows 'Status changed: Pending → In progress'.",
  },
  {
    id: "qa-admin-04",
    category: "Admin",
    title: "Mark complete blocked when no Delivery row exists",
    description:
      "Try to mark an IN_REVIEW order complete without saving the delivery form.",
    expected:
      "Click 'Mark complete' → toast error 'Save the delivery details before marking complete'. Status doesn't change.",
  },
  {
    id: "qa-admin-05",
    category: "Admin",
    title: "Delivery form save is non-destructive",
    description:
      "Open the delivery form for an in-progress order. Save partial details.",
    expected:
      "Toast 'Delivery saved'. Customer is NOT notified yet (no email). Status stays in_progress / in_review.",
  },
  {
    id: "qa-admin-06",
    category: "Admin",
    title: "Refund issues Stripe refund and cancels order",
    description:
      "On a paid order, click Refund, optionally add a reason, confirm.",
    expected:
      "Stripe dashboard shows a refund matching the order. Order status flips to CANCELLED. Customer receives cancellation email mentioning the refund. Audit log shows 'Order refunded' with stripeRefundId.",
  },
  {
    id: "qa-admin-07",
    category: "Admin",
    title: "Reopen completed order works",
    description:
      "On a COMPLETED order, click 'Reopen' and confirm.",
    expected:
      "Status flips back to IN_REVIEW. Customer's Build Room reverts on next poll. Re-completing later re-stamps completedAt and the customer doesn't get a duplicate delivery email (Resend dedupes within 24h).",
  },
  {
    id: "qa-admin-08",
    category: "Admin",
    title: "Extend deadline always emails the customer",
    description:
      "Click 'Extend deadline', pick a date 24h further out, optionally add a reason, save.",
    expected:
      "Customer receives an email showing the new deadline. Sidebar updates. Audit log shows 'Deadline updated' with deltaHours.",
  },
  {
    id: "qa-admin-09",
    category: "Admin",
    title: "Optimistic concurrency on simultaneous status flips",
    description:
      "In two browser tabs, click 'Start build' on the same PENDING order at the same time.",
    expected:
      "One tab succeeds. The other shows toast 'This order was updated by someone else. Refreshing.' and the table refreshes to live state.",
  },
  {
    id: "qa-admin-10",
    category: "Admin",
    title: "CSV export downloads orders snapshot",
    description:
      "Visit /api/admin/export/orders (signed in as admin).",
    expected:
      "Browser downloads a CSV file. Open it: column headers match the schema. One row per order. CRLF line endings.",
  },

  /* ================================ Edges =================================== */
  {
    id: "qa-edge-01",
    category: "Edges",
    title: "Cross-customer access returns 404",
    description:
      "While signed in as Customer A, paste an order URL that belongs to Customer B (e.g. /orders/xyz).",
    expected:
      "404 page renders (NOT 401/403). The branded not-found page shows 'Back to dashboard'.",
  },
  {
    id: "qa-edge-02",
    category: "Edges",
    title: "Mobile pass at 375px on the delivered Build Room",
    description:
      "Open the delivered Build Room on a phone (or DevTools at 375px).",
    expected:
      "Hero CTA is full-width and ≥48px tall. Sections stack cleanly. Markdown instructions wrap long URLs without horizontal page scroll. Credential reveal toggle is thumb-friendly.",
  },
  {
    id: "qa-edge-03",
    category: "Edges",
    title: "Health endpoint returns ok",
    description:
      "Open /api/health in a browser tab.",
    expected:
      "JSON response { \"status\": \"ok\" } with HTTP 200. (Or { \"status\": \"degraded\" } and 503 if any subsystem is down — that's still passing the health-check contract.)",
  },
  {
    id: "qa-edge-04",
    category: "Edges",
    title: "Rate limit kicks in after 6 rapid checkout attempts",
    description:
      "From the wizard review, click Proceed to Payment 6+ times in a minute (cancelling each).",
    expected:
      "The 6th request returns 429 (you'll see it in DevTools Network). UI surfaces toast 'You're doing that a lot — try again in a moment.'",
  },
];

export function findTestCase(id: string): QaTestCase | undefined {
  return QA_TEST_CASES.find((t) => t.id === id);
}

// Stable count surfaced to the start screen so testers know what they're
// signing up for before they begin.
export const QA_TEST_CASE_COUNT = QA_TEST_CASES.length;
